import * as React from 'react';
import {
  Box,
  HeadingText,
  Modal,
  StackLayout,
} from '@leagueplatform/genesis-core';

export interface BulkActionReportingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  header?: React.ReactNode;
  /**
   * Content rendered in the body area of the modal.
   *
   * @example
   * <BulkActionReportingModal title="Copying to Production" header={...} footer={...}>
   *   <StatusReportTable
   *     items={items}
   *     columns={columns}
   *   />
   * </BulkActionReportingModal>
   */
  children: React.ReactNode;
  /**
   * Optional footer content rendered below the scrollable body area.
   * Accepts any ReactNode — the consumer owns the footer layout.
   */
  footer?: React.ReactNode;
}

/**
 * A layout shell for bulk action reporting modals (e.g., Bulk Promote,
 * Import/Export).
 *
 * Layout:
 * - Header: Title + optional header content
 * - Body: Content area that fills available space
 * - Footer: Consumer-provided ReactNode rendered below the body
 *
 * Pass a `StatusReportTable` as children for status reporting content.
 */
export const BulkActionReportingModal = ({
  open,
  onOpenChange,
  title,
  header,
  children,
  footer,
}: BulkActionReportingModalProps) => {
  const handleInteractOutside = (event: Event) => {
    event.preventDefault();
  };

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange}>
      <Modal.Content
        showCloseButton={false}
        onInteractOutside={handleInteractOutside}
        layout="fullscreen"
        css={{
          '& .GDS-modal-content': {
            height: '100%',
          },
        }}
      >
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto minmax(0, 1fr) auto',
            height: '100%',
          }}
        >
          <StackLayout
            spacing="$half"
            css={{ marginBlockEnd: '$threeQuarters', flexShrink: 0 }}
          >
            <Modal.Title>
              <HeadingText level="1" size="lg">
                {title}
              </HeadingText>
            </Modal.Title>

            {header && <Box css={{ width: '100%' }}>{header}</Box>}
          </StackLayout>

          {children}

          {footer && (
            <Box css={{ marginBlockStart: '$oneAndHalf' }}>{footer}</Box>
          )}
        </Box>
      </Modal.Content>
    </Modal.Root>
  );
};
